import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { fromEvent, ReplaySubject, Subject } from 'rxjs';
import {
    filter,
    map,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromDataServices from '../../../data-services';
import * as fromInvoiceSetup from '../../../data-services/invoice/setup';
import {
    Accordion,
    DialogService,
    DynamicDialogRef,
    ResponseStatus,
    SelectItem,
    uniqFilter
} from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';
import { InvoiceSessionUpdateDialogComponent } from '../invoice-session-update-dialog/invoice-session-update-dialog.component';
import { InvoiceSetupUpdateDialogComponent } from '../invoice-setup-update-dialog/invoice-setup-update-dialog.component';

@Component({
    selector: 'ef-invoice-setup-presenter',
    templateUrl: './invoice-setup-presenter.component.html',
    styleUrls: ['./invoice-setup-presenter.component.scss'],
    standalone: false
})
export class InvoiceSetupPresenterComponent
    implements OnInit, OnDestroy, OnChanges
{
    constructor(
        private log: LoggingService,
        private cdRef: ChangeDetectorRef,
        private dialogService: DialogService
    ) {}
    @Input() invoiceProperties: SelectItem[] = [];
    @Input() accountClassProperties: SelectItem[] = [];
    @Input() signatureLineProperties: SelectItem[] = [];
    @Input() invoiceViewOptions: SelectItem[] = [];
    @Input() disclaimerProperties: SelectItem[] = [];
    @Input() paymentTermsProperties: SelectItem[] = [];
    @Input() selections: fromInvoiceSetup.Selections.InvoiceSelections[];
    @Input() states: SelectItem[];
    @Input() addressTypes: SelectItem[];
    @Input() addressSetup: fromDataServices.Address.Address[];
    @Input() invoices: fromInvoiceSetup.Invoice.InvoiceSetup[];
    @Input() addresses: SelectItem[];
    @Input() remitAddresses: SelectItem[];
    @Input() billingAddresses: SelectItem[];
    @Input() invoiceSelections: SelectItem[];
    @Input() invoiceContracts: SelectItem[];
    @Input() chargeNumbers: SelectItem[];
    @Input() invoiceTypes: SelectItem[];
    @Input() sessions: fromInvoiceSetup.Session.InvoiceSessionSetup[];
    @Input() invoiceSessions: SelectItem[];
    @Output() SaveSelection = new EventEmitter();
    @Output() DeleteSelection = new EventEmitter();
    @Output() SaveAddress = new EventEmitter();
    @Output() DeleteAddress = new EventEmitter();
    @Output() SaveInvoiceOptions = new EventEmitter();
    @Output() SelectInvoiceOptionsSubject = new ReplaySubject<
        fromInvoiceSetup.Invoice.InvoiceSetup[]
    >(1);
    @Output() SelectVoucherOptionsSubject = new ReplaySubject<
        fromInvoiceSetup.Session.InvoiceSessionSetup[]
    >(1);
    @Output() SaveSession = new EventEmitter();
    @Output() UpdateBulkInvoiceSetups = new Subject<
        fromInvoiceSetup.Invoice.InvoiceSetup[]
    >();
    @Output() UpdateBulkVouchers = new Subject<
        fromInvoiceSetup.Session.InvoiceSessionSetup[]
    >();
    @Output() ApplyBulkUpdates = new ReplaySubject<any>(1);
    @Output() ApplyBulkVoucherUpdates = new ReplaySubject<any>(1);

    @ViewChild('accordion', { static: true }) accordion: Accordion;
    @ViewChild('updateMultiple', { static: true })
    updateMultipleButton: ElementRef;
    @ViewChild('updateMultipleSessions', { static: true })
    updateMultipleSessionsButton: ElementRef;

    public tabHeight = '15vh';
    private active = true;
    activeIndex: number[] = [];
    bulkVoucherOpen;
    tabsDisabled = true;
    displayAddress = false;
    displayFooter = false;
    elements: HTMLCollectionOf<Element>;
    private updateDialogRef: DynamicDialogRef;
    ngOnInit(): void {
        this.handleOnOpen([2]);
        this.setupSubscriptions();
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    onGetElements(event: HTMLCollectionOf<Element>) {
        this.elements = event;
    }
    ngOnChanges(changes: SimpleChanges) {
        this.log.verbose({
            type: 'InvoiceSetupPresenterComponent:ngOnChanges',
            changes
        });
        if (changes.invoices && changes.invoices.currentValue) {
            if (changes.invoices.currentValue.length > 0) {
                this.log.verbose({
                    type: 'InvoiceSetupPresenterComponent:ngOnChanges:invoices',
                    invoices: changes.invoices,
                    activeIndex: this.activeIndex
                });
                this.activeIndex =
                    // uniqBy([...this.activeIndex, 0, 1], x => x);
                    [...this.activeIndex, 0].filter(uniqFilter);
                this.handleOnOpen(this.activeIndex);
                this.log.verbose({
                    type: 'InvoiceSetupPresenterComponent:ngOnChanges:invoices',
                    invoices: changes.invoices,
                    activeIndex: this.activeIndex
                });
                this.tabsDisabled = false;
            } else {
                this.activeIndex = [];
                this.handleOnOpen(this.activeIndex);
                this.tabsDisabled = true;
            }
            this.cdRef.detectChanges();
        }
    }
    setupSubscriptions() {
        this.SelectVoucherOptionsSubject.pipe(
            takeWhile(() => this.active)
        ).subscribe(x =>
            this.log.verbose({
                type: 'InvoiceSetupPresenterComponent:SelectVoucherOptions',
                x
            })
        );
        this.ApplyBulkUpdates.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.SelectInvoiceOptionsSubject),
            tap(([updates, invoices]) =>
                this.log.verbose({
                    type: 'InvoiceSetupPresenterComponent:ApplyBulkUpdates',
                    updates,
                    invoices
                })
            ),
            filter(([updates, invoices]) => invoices?.length > 0)
        ).subscribe(([updates, invoices]) => {
            this.invoices = this.invoices.map(invoice => ({
                ...invoice,
                Status: {
                    Message: 'Saving...',
                    StatusCode: ResponseStatus.Loading
                }
            }));
            this.UpdateBulkInvoiceSetups.next(
                invoices.map(invoice => ({
                    ...invoice,
                    Accountclass: updates.Accountclass || invoice.Accountclass,
                    Billaddrid: updates.Billaddrid || invoice.Billaddrid,
                    Disclaimerid: updates.Disclaimerid || invoice.Disclaimerid,
                    Remitaddrid: updates.Remitaddrid || invoice.Remitaddrid,
                    Invprefix: updates.Invprefix || invoice.Invprefix,
                    Levelspent: updates.Levelspent || invoice.Levelspent,
                    Invoiceviewoptionid:
                        updates.Invoiceviewoptionid ||
                        invoice.Invoiceviewoptionid,
                    Showcosttypesummaryburden:
                        updates.Showcosttypesummaryburden !== null
                            ? updates.Showcosttypesummaryburden
                            : invoice.Showcosttypesummaryburden,
                    Showcosttypesummary:
                        updates.Showcosttypesummary !== null
                            ? updates.Showcosttypesummary
                            : invoice.Showcosttypesummary,
                    Showcontractbalance:
                        updates.Showcontractbalance !== null
                            ? updates.Showcontractbalance
                            : invoice.Showcontractbalance,
                    Showburdens:
                        updates.Showburdens !== null
                            ? updates.Showburdens
                            : invoice.Showburdens,
                    Showcurrentactivityonly:
                        updates.Showcurrentactivityonly !== null
                            ? updates.Showcurrentactivityonly
                            : invoice.Showcurrentactivityonly,
                    Showfundingbalance:
                        updates.Showfundingbalance !== null
                            ? updates.Showfundingbalance
                            : invoice.Showfundingbalance,
                    ShowEIN:
                        updates.ShowEIN === null
                            ? invoice.ShowEIN
                            : updates.ShowEIN,
                    Showindirectrates:
                        updates.Showindirectrates === null
                            ? invoice.Showindirectrates
                            : updates.Showindirectrates,
                    Showlaborrates:
                        updates.Showlaborrates === null
                            ? invoice.Showlaborrates
                            : updates.Showlaborrates,
                    Siglineid: updates.Siglineid || invoice.Siglineid
                }))
            );
            this.SelectInvoiceOptionsSubject.next([]);
        });
        this.ApplyBulkVoucherUpdates.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.SelectVoucherOptionsSubject),
            tap(([updates, vouchers]) =>
                this.log.verbose({
                    type: 'InvoiceSetupPresenterComponent:ApplyBulkVoucherUpdates',
                    updates,
                    vouchers
                })
            )
        ).subscribe(([updates, vouchers]) => {
            this.UpdateBulkVouchers.next(
                vouchers.map(voucher => ({
                    ...voucher,
                    Remitaddrid: updates.Remitaddrid || voucher.Remitaddrid,
                    Voucheraddrid:
                        updates.Voucheraddrid || voucher.Voucheraddrid,
                    Contractdesc: updates.Contractdesc || voucher.Contractdesc,
                    Feeseparate:
                        updates.Feeseparate === null
                            ? voucher.Feeseparate
                            : updates.Feeseparate,
                    ShowCN:
                        updates.ShowCN === null
                            ? voucher.ShowCN
                            : updates.ShowCN
                }))
            );
        });
        fromEvent(this.updateMultipleButton.nativeElement, 'click')
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(
                    this.SelectInvoiceOptionsSubject.pipe(
                        map(items => items.length)
                    )
                )
            )
            .subscribe(([click, count]) => {
                this.updateDialogRef = this.dialogService.open(
                    InvoiceSetupUpdateDialogComponent,
                    {
                        header: 'Update Multiple Invoice Format Rows',
                        closable: false,
                        closeOnEscape: false,
                        showHeader: false,
                        modal: true,
                        data: {
                            addresses: this.addresses,
                            disclaimerProperties: this.disclaimerProperties,
                            signatureLineProperties:
                                this.signatureLineProperties,
                            accountClassProperties: this.accountClassProperties,
                            invoiceViewOptions: this.invoiceViewOptions,
                            count
                        }
                    }
                );
                this.updateDialogRef.onClose.subscribe(value => {
                    this.log.verbose({
                        type: 'InvoiceSetupUpdateDialogComponent:afterClosed',
                        value
                    });
                    if (value) {
                        this.ApplyBulkUpdates.next(value);
                    }
                });
            });
        fromEvent(this.updateMultipleSessionsButton.nativeElement, 'click')
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.updateDialogRef = this.dialogService.open(
                    InvoiceSessionUpdateDialogComponent,
                    {
                        header: 'Update Multiple Voucher Records',
                        closable: false,
                        closeOnEscape: false,
                        showHeader: false,
                        modal: true,
                        data: {
                            addresses: this.addresses
                        }
                    }
                );
                this.updateDialogRef.onClose.pipe(take(1)).subscribe(value => {
                    this.log.verbose({
                        type: 'InvoiceSessionUpdateDialogComponent:afterClosed',
                        value
                    });
                    if (value) {
                        this.ApplyBulkVoucherUpdates.next(value);
                    }
                });
            });
    }
    handleOnOpen($event) {
        // this is the best way to handle accordion open/close
        // baseheight may need adjustment depending on anything outside the tabs
        this.log.verbose({
            type: 'InvoiceSetupPresenterComponent:handleOnOpen',
            $event,
            activeIndex: this.activeIndex
        });
        const baseHeight = 50;

        this.tabHeight = `${
            baseHeight / ($event && $event.length ? $event.length : 1) - 1
        }vh`;
    }

    invoiceHeight = () => `calc(${this.tabHeight} - 2.5em)`;
    voucherHeight = () => `calc(${this.tabHeight} - 2.5em)`;
}
